import { useState, useCallback, useMemo, useEffect } from "react";
import { useQuery, useQueries, useQueryClient } from "@tanstack/react-query";
import { listOrders, getOrderById, advanceOrder, type OrderRecord, type OrderDetailRecord } from "@veorest/api-client";
import {
  Clock, Store, MessageCircle, ShoppingBag, Play, CheckCircle2, ChefHat,
} from "lucide-react";
import { useTenant } from "../hooks/useTenant";
import { useI18n } from "../i18n";
import { cacheOrders, getCachedOrdersSync, useBackendReachable, queueAction } from "@veorest/pwa";

type SourceMeta = { icon: React.FC<{ size?: number }>; label: string; color: string };
const SOURCE_META: Record<string, SourceMeta> = {
  marketplace_app: { icon: (p) => <Store size={p?.size ?? 14} />, label: "OELOX", color: "#d70f64" },
  storefront: { icon: (p) => <Store size={p?.size ?? 14} />, label: "OELOX", color: "#d70f64" },
  whatsapp_ai: { icon: (p) => <MessageCircle size={p?.size ?? 14} />, label: "WhatsApp", color: "#25D366" },
  whatsapp: { icon: (p) => <MessageCircle size={p?.size ?? 14} />, label: "WhatsApp", color: "#25D366" },
  staff: { icon: (p) => <ShoppingBag size={p?.size ?? 14} />, label: "Local", color: "#6366f1" },
  phone: { icon: (p) => <ShoppingBag size={p?.size ?? 14} />, label: "Local", color: "#6366f1" },
};
const FALLBACK: SourceMeta = { icon: (p) => <ShoppingBag size={p?.size ?? 14} />, label: "Local", color: "#6366f1" };
function srcMeta(s?: string): SourceMeta { return s ? (SOURCE_META[s] ?? FALLBACK) : FALLBACK; }

function elapsed(iso: string, justNowLabel: string): string {
  const diff = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60000));
  if (diff < 1) return justNowLabel;
  if (diff < 60) return `${diff}m`;
  const h = Math.floor(diff / 60);
  const m = diff % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

type KdsGroup = "new" | "preparing" | "ready";

function groupOrder(order: OrderRecord): KdsGroup {
  if (order.status === "ready_for_dispatch" || order.status === "blasted_to_drivers") return "ready";
  if (order.status === "in_kitchen" || order.status === "approved") return "preparing";
  return "new"; // pending_cashier_approval, accepted
}

const GROUP_COLORS: Record<KdsGroup, string> = { new: "#f59e0b", preparing: "#3b82f6", ready: "#10b981" };

function groupLabelKey(g: KdsGroup): string {
  if (g === "new") return "kds.new";
  if (g === "preparing") return "kds.preparing";
  return "kds.readyCol";
}

export default function KdsPage() {
  const { t } = useI18n();
  const { tenant } = useTenant();
  const tenantId = tenant?.id ?? "";
  const qc = useQueryClient();
  const reachable = useBackendReachable();

  const [busyOrder, setBusyOrder] = useState<string | null>(null);

  const { data: orderList } = useQuery({
    queryKey: ["pos-kds-list", tenantId],
    queryFn: async () => {
      const [pending, kitchen, ready] = await Promise.all([
        listOrders({ tenantId: tenantId!, filter: "pending", limit: 30 }),
        listOrders({ tenantId: tenantId!, filter: "kitchen", limit: 30 }),
        listOrders({ tenantId: tenantId!, filter: "ready", limit: 20 }),
      ]);
      const seen = new Set<string>();
      const all: OrderRecord[] = [];
      for (const o of [...pending.orders, ...kitchen.orders, ...ready.orders]) {
        if (!seen.has(o.id)) { seen.add(o.id); all.push(o); }
      }
      return all.filter((o) => o.status !== "draft" && o.status !== "cancelled" && o.status !== "delivered" && o.status !== "completed");
    },
    enabled: Boolean(tenantId),
    refetchInterval: reachable ? 15_000 : false,
    placeholderData: () => {
      // Use cached orders for instant render before network responds
      const cached = getCachedOrdersSync(tenantId);
      if (cached.length > 0) return cached as unknown as OrderRecord[];
      return undefined;
    },
  });

  const orders = orderList ?? [];

  // Cache orders to IndexedDB on successful fetch
  useEffect(() => {
    if (orderList && orderList.length > 0 && tenantId) {
      cacheOrders(tenantId, orderList.map((o) => ({
        id: o.id, tenant_id: tenantId, displayNumber: o.displayNumber,
        customer_name: o.customer_name, status: o.status, deskStep: o.deskStep,
        total: o.total, source: o.source, fulfillment_type: o.fulfillment_type,
        created_at: o.created_at, cached_at: Date.now(),
      })));
    }
  }, [orderList, tenantId]);

  // Batch-fetch details for each order to get items
  const detailQueries = useQueries({
    queries: orders.map((o) => ({
      queryKey: ["pos-kds-detail", tenantId, o.id],
      queryFn: () => getOrderById(tenantId!, o.id),
      enabled: Boolean(tenantId),
      staleTime: 10_000,
    })),
  });

  const ordersWithItems: (OrderRecord & { items?: OrderDetailRecord["items"] })[] = useMemo(
    () => orders.map((o, i) => ({ ...o, items: detailQueries[i]?.data?.items })),
    [orders, detailQueries]
  );

  const grouped = useMemo(() => {
    const g: Record<KdsGroup, (OrderRecord & { items?: OrderDetailRecord["items"] })[]> = { new: [], preparing: [], ready: [] };
    for (const o of ordersWithItems) g[groupOrder(o)].push(o);
    return g;
  }, [ordersWithItems]);

  const handleAction = useCallback(async (order: OrderRecord) => {
    setBusyOrder(order.id);
    const nextAction = groupOrder(order) === "new" ? "start_cooking" : "mark_ready";

    if (!reachable) {
      // Offline: queue action, optimistically move card
      queueAction("advance_order", { tenantId: tenantId!, orderId: order.id, step: nextAction, fulfillmentType: order.fulfillment_type });
      setBusyOrder(null);
      qc.invalidateQueries({ queryKey: ["pos-kds-list", tenantId] });
      return;
    }

    try {
      await advanceOrder(tenantId!, order.id, nextAction as "start_cooking" | "mark_ready", order.fulfillment_type);
      qc.invalidateQueries({ queryKey: ["pos-kds-list", tenantId] });
    } catch { /* Network/API error — keep card in current column */ }
    finally { setBusyOrder(null); }
  }, [tenantId, qc, reachable]);

  if (!tenantId) return <div style={{ padding: 20, color: "var(--text-muted)" }}>{t("loading")}</div>;

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column", background: "var(--surface-muted)", color: "var(--text-primary)", overflow: "hidden" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 14px", background: "var(--surface-raised)", borderBottom: "2px solid var(--border)", flexShrink: 0 }}>
        <ChefHat size={18} style={{ color: "var(--accent)" }} />
        <span style={{ fontWeight: 800, fontSize: 14 }}>{t("kds.title")}</span>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text-secondary)" }}>{orders.length} {t("kds.orders")}</span>
      </div>

      {/* Grid: responsive columns */}
      <div style={{ flex: 1, display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, padding: 10, overflow: "auto", minHeight: 0 }}>
        {(Object.keys(grouped) as KdsGroup[]).map((group) => {
          const color = GROUP_COLORS[group];
          const list = grouped[group];
          return (
            <div key={group} style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 0 }}>
              {/* Group header */}
              <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", background: color + "25", borderRadius: "var(--radius-md)", flexShrink: 0 }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: color }} />
                <span style={{ fontWeight: 800, fontSize: 13, color: color }}>{t(groupLabelKey(group))}</span>
                <span style={{ marginInlineStart: "auto", fontSize: 11, fontWeight: 700, color: color, opacity: 0.8 }}>{list.length}</span>
              </div>

              {/* Order cards */}
              {list.length === 0 ? (
                <div style={{ padding: 20, textAlign: "center", color: "var(--text-muted)", fontSize: 12, fontWeight: 600, border: "2px dashed var(--border)", borderRadius: "var(--radius-md)" }}>
                  {t("kds.noOrders")}
                </div>
              ) : (
                list.map((order) => {
                  const src = srcMeta(order.source);
                  const SrcIcon = src.icon;
                  const busy = busyOrder === order.id;
                  const orderGroup = groupOrder(order);
                  return (
                    <div key={order.id} style={{
                      background: "var(--surface-raised)", border: "2px solid var(--border)", borderRadius: "var(--radius-lg)",
                      overflow: "hidden", flexShrink: 0,
                    }}>
                      {/* Card header */}
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderBottom: "2px solid var(--border)" }}>
                        <div style={{
                          width: 34, height: 34, borderRadius: 8, flexShrink: 0,
                          background: src.color + "25", display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          <SrcIcon size={16} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontWeight: 900, fontSize: 14, color: "var(--text-primary)" }}>#{order.displayNumber}</span>
                            <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 999, background: src.color + "25", color: src.color }}>{src.label}</span>
                          </div>
                          <div style={{ display: "flex", gap: 8, fontSize: 11, color: "var(--text-secondary)", marginTop: 1, fontWeight: 600 }}>
                            <span>{order.customer_name}</span>
                            <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Clock size={10} />{elapsed(order.created_at, t("kds.justNow"))}</span>
                          </div>
                        </div>
                      </div>

                      {/* Items */}
                      <div style={{ padding: "8px 12px", maxHeight: 160, overflowY: "auto" }}>
                        {order.items?.slice(0, 8).map((item: any) => (
                          <div key={item.id ?? item.menu_item_id} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0", fontSize: 13, borderBottom: "1px solid var(--border-subtle)" }}>
                            <span style={{ fontWeight: 700, color: "var(--text-primary)" }}>{item.quantity}× {item.item_name}</span>
                          </div>
                        ))}
                        {(order.items?.length ?? 0) > 8 && (
                          <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-secondary)", padding: "3px 0" }}>+{(order.items?.length ?? 0) - 8} {t("kds.moreItems")}</div>
                        )}
                      </div>

                      {/* Action */}
                      <div style={{ padding: "8px 12px", borderTop: "2px solid var(--border)", display: "flex", gap: 6 }}>
                        {orderGroup === "new" && (
                          <button onClick={() => handleAction(order)} disabled={busy} style={{
                            flex: 1, padding: "8px", borderRadius: "var(--radius-md)", border: "none",
                            background: "#3b82f6", color: "#fff", fontWeight: 700, fontSize: 13, cursor: busy ? "not-allowed" : "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                          }}>
                            <Play size={18} />{busy ? t("kds.starting") : t("kds.startCooking")}
                          </button>
                        )}
                        {orderGroup === "preparing" && (
                          <button onClick={() => handleAction(order)} disabled={busy} style={{
                            flex: 1, padding: "8px", borderRadius: "var(--radius-md)", border: "none",
                            background: "#10b981", color: "#fff", fontWeight: 700, fontSize: 13, cursor: busy ? "not-allowed" : "pointer",
                            display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                          }}>
                            <CheckCircle2 size={18} />{busy ? t("kds.marking") : t("kds.markReady")}
                          </button>
                        )}
                        {orderGroup === "ready" && (
                          <div style={{ flex: 1, padding: "8px", textAlign: "center", color: "#10b981", fontWeight: 700, fontSize: 13 }}>
                            {t("kds.readyForPickup")}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
